<template>
  <BaseCard
    type="elevated"
    class="group relative mt-1 flex cursor-pointer flex-row items-center overflow-hidden p-4 transition-all"
    @click="handleSuperpowerSurvey"
  >
    <div class="ease-slow-effect relative mr-4 h-22 w-22 self-start transition-all duration-400">
      <div
        class="animate-rotate absolute top-1/2 left-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2"
      >
        <BaseShape shape="7-sided-cookie" class="animate-bouncy-spin text-primary h-full w-full" />
      </div>
      <BaseIcon
        size="32"
        class="text-on-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        >self_improvement</BaseIcon
      >
    </div>
    <div class="absolute top-1 right-1 flex flex-row justify-end gap-0.5">
      <BaseIconButton color="standard" size="XS" icon="info" @click.stop="openDialog" />
      <BaseIconButton
        color="standard"
        size="XS"
        icon="close"
        @click.stop="openDismissSuperpowerTaskDialog"
      />
    </div>
    <div class="flex w-full flex-col">
      <h3 class="text-display-medium mb-1 transition-all">Bestimme deine Superkraft</h3>
      <p class="text-body-medium">
        Finde heraus, was dir Energie gibt und wo deine besonderen Stärken liegen.
      </p>
    </div>
    <BaseIcon size="36" class="ml-auto">chevron_right</BaseIcon>
    <BaseStateLayer />
    <BaseDialog
      type="basic"
      title="Was ist deine Superkraft?"
      :is-open="isOpen"
      @close="closeDialog"
    >
      <BaseComplexCMSContent
        v-if="superpowerTask.description"
        :content="superpowerTask.description"
        class="[&_h3]:text-headline-medium [&_p]:text-body-large [&_p]:text-on-surface-variant [&_h3]:mb-2"
      />
      <template #dismiss>
        <BaseTextButton color="text" @click="closeDialog">{{ $t('common.cancel') }}</BaseTextButton>
      </template>
    </BaseDialog>
    <BaseDialog
      type="basic"
      title="Hinweis entfernen"
      description="Möchtest du den Hinweis zum Herausfinden deiner Superkraft entfernen? Du kannst deine Superkraft jederzeit mittels Selbstfeedback herausfinden."
      :is-open="isDismissSuperpowerTaskDialogOpen"
      @close="closeDismissSuperpowerTaskDialog"
    >
      <template #confirm>
        <BaseTextButton color="text" @click="dismissSuperpowerTask()">{{
          $t('common.remove')
        }}</BaseTextButton>
      </template>
      <template #dismiss>
        <BaseTextButton color="text" @click="closeDismissSuperpowerTaskDialog">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
  </BaseCard>
</template>
<script setup lang="ts">
  import { useRouter } from 'vue-router'
  import BaseComplexCMSContent from '../../../core/components/base/complex-content/BaseComplexCMSContent.vue'
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import { useUserStore } from '../../../stores/user.store'
  import { useStartSelfFeedback } from '@/modules/start-self-feedback/use-start-self-feedback'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'  
  import type { Dashboard } from '../models/dashboard'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import BaseIcon from '../../../core/components/base/icon/BaseIcon.vue'
  import BaseShape from '../../../core/components/base/shapes/BaseShape.vue'
  import { useLocalStorage } from '@vueuse/core'
  import BaseIconButton from '../../../core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseCard from '@/core/components/base/card/BaseCard.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'

  interface Props {
    superpowerTask: NonNullable<Dashboard['superpowerTask']>
  }

  const props = defineProps<Props>()

  const router = useRouter()
  const { user } = useUserStore()
  const { selfFeedbackFormData, startSelfFeedback, clearForm } = useStartSelfFeedback()
  const { isOpen, closeDialog, openDialog } = useDialog()
  const {
    isOpen: isDismissSuperpowerTaskDialogOpen,
    closeDialog: closeDismissSuperpowerTaskDialog,
    openDialog: openDismissSuperpowerTaskDialog,
  } = useDialog()

  const dismissedSuperpowerTask = useLocalStorage('dismissed-superpower-task', false)

  const dismissSuperpowerTask = () => {
    dismissedSuperpowerTask.value = true
  }

  const handleSuperpowerSurvey = async () => {
    if (props.superpowerTask.surveyId) {
      router.push({
        name: 'feedback',
        params: { surveyId: props.superpowerTask.surveyId, receiverId: user.id },
      })
      return
    }
    selfFeedbackFormData.templateIds = [props.superpowerTask.feedbackTopicId]
    selfFeedbackFormData.title = props.superpowerTask.title

    await startSelfFeedback()

    clearForm()
  }
</script>
